/** @module workHistory */

import moment from "moment";
import uuid from "uuid/v4";

import { getMonthAndYearFromDate, getUTCDate } from ".";
import { toInt } from "./helpers";

/**
 * MODEL -> UI
 *
 * Converts the workHistory object from the form as represented in the data store to the
 * model structure of the ui
 *
 * A unique id is created if the id property does not exist
 *
 * @param {object} workHistory the data as stored by the server
 * @returns {object} The data as stored in the ui
 */
export const workHistoryToUiModel = ({
  dateFrom,
  dateTo,
  currentlyWorkHere = false,
  ...rest
}) => {
  const from = getMonthAndYearFromDate(dateFrom);
  const to = getMonthAndYearFromDate(dateTo);
  return {
    toMonth: to.month,
    toYear: to.year,
    fromMonth: from.month,
    fromYear: from.year,
    currentlyWorkHere,
    id: uuid(),
    ...rest,
  };
};

/**
 * UI -> MODEL
 *
 * Converts the workHistory object from the form as represented in the ui to the
 * model structure as stored in the database
 *
 * @param {object} workHistory the data as stored by the ui
 * @returns {object} The data as stored on the server
 */
export const uiModelToWorkHistory = ({
  id = uuid(),
  title,
  companyName,
  fromMonth,
  toMonth,
  fromYear,
  toYear,
  currentlyWorkHere,
  description,
}) => {
  // Months are based on a 0 indexed array
  const dateFrom = getUTCDate(new Date(toInt(fromYear), toInt(fromMonth), 1));
  const dateTo = getUTCDate(
    moment(new Date(toInt(toYear), toInt(toMonth), 1))
      .endOf("month")
      .toDate()
  );
  return {
    id,
    title,
    companyName,
    dateFrom,
    dateTo,
    currentlyWorkHere,
    description,
  };
};

export const getDateForCurrent = () =>
  new Date().setMonth(new Date().getMonth() + 1);

export const getToDate = ({ dateTo, currentlyWorkHere }) =>
  currentlyWorkHere ? getDateForCurrent() : dateTo;

export const workHistoryDateDescend = (a, b) => getToDate(b) - getToDate(a);

export const addIdToWorkHistory = (workHistoryItem) => ({
  id: uuid(),
  ...workHistoryItem,
});

export const loadWorkHistoryUi = (workHistory, id) =>
  workHistoryToUiModel(workHistory.filter((history) => history.id === id)[0]);

const isNotEmpty = (item) => item !== "";

export const isWorkHistoryValid = ({
  fromMonth,
  fromYear,
  toMonth,
  toYear,
  currentlyWorkHere,
}) => {
  const isFromDateCorrect = isNotEmpty(fromMonth) && isNotEmpty(fromYear);
  const isToDateCorrect = isNotEmpty(toMonth) && isNotEmpty(toYear);
  return isFromDateCorrect && (currentlyWorkHere || isToDateCorrect);
};
